import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbsOptions } from "../../shared/models/breadcrumbs-model";
import { Router } from '@angular/router';
import { OrdersService } from '../services/orders/orders.service';
import { OrderData, OrderProjection } from '../models/order';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CustomHeader } from '../../shared/components/custom-datepicker-header/custom-datepicker-header.component';
import { AlertComponent } from '../../shared/components/dialogs/alert/alert.component';
import { ConfirmDialogComponent } from '../../shared/components/dialogs/confirm/confirm.component';
import { UsersService } from '../../users/services/users.service';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ConfirmFormDialogComponent } from '../../shared/components/dialogs/confirm-form/confirm-form.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LoaderDialogComponent } from '../../shared/components/loader-dialog/loader-dialog.component';
import { finalize } from 'rxjs';
import { LocalStorageService } from '../../shared/services/local-storage.service';

@Component({
  selector: 'app-orders-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatOptionModule,
    MatDialogModule,
    TranslateModule,
    FlexLayoutModule,
    MatTableModule,
    MatSelectModule,
    BreadcrumbsComponent,
    MatPaginator,
    MatDatepickerModule,
    MatNativeDateModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
  ],
  templateUrl: './orders-dashboard.component.html',
  styleUrl: './orders-dashboard.component.scss',
})
export class OrdersDashboardComponent implements OnInit {
  customHeader = CustomHeader;

  users: any[] = [];
  filteredUsers: any[] = [];
  public showRequesterButtons!: boolean;
  public showApprovalButtons!: boolean;

  pageable: PageEvent = {
    pageIndex: 0,
    pageSize: 5,
    length: 0,
  };

  breadcrumbsData: BreadcrumbsOptions = {
    title: 'Painel de pedidos',
    buttons: [
      {
        label: 'button.new-order',
        style: 'blue',
        router: '/new-order'
      },
    ]
  };

  @ViewChild('form') orderFormViewChild: any;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  orderForm: FormGroup;

  tableData = new MatTableDataSource<OrderData>();

  displayedColumns: string[] = [
    'orders',
    'projectType',
    'projectDescription',
    'requestDate',
    'purchaseDate',
    'status',
    'action',
  ];
  columnWidths: { [key: string]: string } = {};

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private ordersService: OrdersService,
    private usersService: UsersService,
    public authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
  ) {
    this.orderForm = this.formBuilder.group({
      orderNumber: [''],
      projectType: [''],
      projectDescription: [''],
      requestDate: [''],
      status: [''],
    });

    let isApproverBudgets = this.authenticationService.isApproverBudgets;
    if (isApproverBudgets) {
      this.breadcrumbsData.buttons![0].hide = true;
    }
  }

  ngOnInit() {
    this.showRequesterButtons = this.authenticationService.isRequester;
    this.showApprovalButtons =
      !this.authenticationService.isRequester &&
      !this.authenticationService.isApproverBudgets;

    if (!this.authenticationService.isRequester) {
      this.displayedColumns.splice(3, 0, 'requester');
      this.orderForm.addControl('requester', this.formBuilder.control(''));
      this.setColumnWidthsWithRequester();
    } else {
      this.setColumnWidthsWithoutRequester();
    }

    this.pageChange(this.pageable);
    this.fillAutocompletes();
    this.removeLocalStorage();
  }

  setColumnWidthsWithRequester() {
    this.columnWidths = {
      orders: '5%',
      projectType: '5%',
      projectDescription: '26%',
      requestDate: '12%',
      requester: '10%',
      purchaseDate: '12%',
      status: '20%',
      action: '10%',
    };
  }

  setColumnWidthsWithoutRequester() {
    this.columnWidths = {
      orders: '5%',
      projectType: '5%',
      projectDescription: '25%',
      requestDate: '15%',
      purchaseDate: '20%',
      status: '20%',
      action: '10%',
    };
  }

  public disableRequesterAction(order: OrderProjection): boolean {
    return order.status !== 'NOT_SENT';
  }

  public disableApprovalApproveReject(order: OrderProjection): boolean {
    return order.status !== 'WAITING_REQUEST_APPROVAL';
  }

  public disableApprovalEditDelete(order: OrderProjection): boolean {
    const validStatus =
      order.status === 'WAITING_REQUEST_APPROVAL' ||
      order.status === 'WAITING_BUDGET_REQUEST_SUBMISSION';
    return (
      !validStatus ||
      !this.authenticationService.isFromLoggedUser(order.username)
    );
  }

  public disableCancelAction(order: OrderProjection): boolean {
    const status = order.status;

    if (this.authenticationService.isApproverBudgets) {
        return status !== 'WAITING_BUDGET_APPROVAL';
    }

    if (this.authenticationService.isAdmin) {
        const validStatuses = [
            'WAITING_BUDGET_APPROVAL',
            'WAITING_BUDGET_REQUEST_SUBMISSION',
            'WAITING_SUPPLIERS_RESPONSE'
        ];
        return !validStatuses.includes(status);
    }

    const validStatuses = [
        'WAITING_BUDGET_REQUEST_SUBMISSION',
        'WAITING_SUPPLIERS_RESPONSE'
    ];
    return !validStatuses.includes(status);
}

  fillAutocompletes() {
    this.usersService.findUsersWithOrders().subscribe((users) => {
      this.users = users;
      this.filteredUsers = [...users];
    });

    this.orderForm.get('requester')?.valueChanges.subscribe((search) => {
      if (typeof search === 'string') {
        this.filteredUsers = this.users.filter(
          (user) =>
            search === '' ||
            user.name.toLowerCase().includes(search?.toLowerCase())
        );
      }
    });
  }

  showName(entity: any) {
    return entity?.name || '';
  }

  pageChange(event: PageEvent) {
    const requesterId = this.orderForm.value.requester?.id ?? null;

    this.ordersService
      .getOrders(
        event,
        this.orderForm.value.orderNumber,
        this.orderForm.value.projectType,
        this.orderForm.value.projectDescription,
        this.orderForm.value.requestDate,
        requesterId,
        this.orderForm.value.status
      )
      .subscribe({
        next: (response: any) => {
          if (response.content.length === 0) {
            this.dialog.open(AlertComponent, {
              disableClose: true,
              data: {
                message: 'orders-dashboard.no-data-found',
                title: 'alert.information',
                complement: '',
                param: null,
                value: null,
              },
            });
          }

          response.content.forEach((order: any) => {
            order.statusDisplay = this.translateStatus(order.status);
            order.projectDescription = order.projectDescription || ' - ';
          });

          this.tableData.data = response.content;
          this.pageable.length = response.totalElements;
          this.pageable.pageSize = response.pageable.pageSize;
        },
        error: () => {
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: 'error.loading-data',
              title: 'alert.information',
            },
          });
        },
      });
  }

  translateStatus(status: string): string {
    switch (status) {
      case 'WAITING_REQUEST_APPROVAL':
        return this.translateService.instant('status.waiting-request-approval');
      case 'WAITING_BUDGET_REQUEST_SUBMISSION':
        return this.translateService.instant(
          'status.waiting-budget-request-submission'
        );
      case 'WAITING_SUPPLIERS_RESPONSE':
        return this.translateService.instant(
          'status.waiting-suppliers-response'
        );
      case 'WAITING_BUDGET_APPROVAL':
        return this.translateService.instant('status.waiting-budget-approval');
      case 'FINISHED':
        return this.translateService.instant('status.finished');
      case 'REJECTED':
        return this.translateService.instant('status.rejected');
      case 'NOT_SENT':
        return this.translateService.instant('status.not-sent');
      case 'CANCELLED_ORDER':
        return this.translateService.instant('status.cancelled');
      case 'CANCELLED_BUDGET':
        return this.translateService.instant('status.cancelled');
      default:
        return status;
    }
  }

  filterItems() {
    this.paginator.firstPage();
    this.pageChange(this.pageable);
  }

  navigateToDetails(order: OrderProjection) {
    const orderStatus = order.status;
    const isRequester = this.authenticationService.isRequester;
    const projectDescription = order.projectDescription;
    const projectType = order.projectType;

    sessionStorage.setItem('isRequester', JSON.stringify(isRequester));
    sessionStorage.setItem('orderStatus', orderStatus);
    sessionStorage.setItem('projectDescription', projectDescription);
    sessionStorage.setItem('projectType', projectType);

    this.router.navigate(['/order-details', order.id], {
      state: { isRequester, orderStatus, projectDescription, projectType },
    });
  }

  approveOrder(order: OrderProjection) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          message: 'orders-dashboard.approveOrderConfirmation',
          param: { orderId: order.id, projectDescription: order.projectDescription }
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.confirm) {
          this.ordersService.approveOrder(order.id).subscribe({
            next: () => {
              this.dialog.open(AlertComponent, {
                disableClose: true,
                data: {
                  title: 'alert.information',
                  message: 'orders-dashboard.approveOrderSuccess',
                  param: { orderId: order.id, projectDescription: order.projectDescription },
                },
              });
              this.resetTable();
            },
            error: () => {
              this.dialog.open(AlertComponent, {
                disableClose: true,
                data: {
                  title: 'alert.information',
                  message: 'orders-dashboard.approveOrderError',
                  param: { orderId: order.id },
                },
              });
            },
          });
        }
      });
  }

  rejectOrder(order: OrderProjection) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          message: 'orders-dashboard.rejectOrderConfirmation',
          param: { orderId: order.id, projectDescription: order.projectDescription },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirm) {
          const justificationDialogRef = this.dialog.open(
            ConfirmFormDialogComponent,
            {
              data: {
                disableClose: true,
                title: 'alert.rejection-justification',
                message: 'alert.rejection-dialog-description',
              },
            }
          );

          justificationDialogRef.afterClosed().subscribe((result) => {
            if (result?.confirm) {
              const loader = this.dialog.open(LoaderDialogComponent, {
                disableClose: true,
              });
              this.ordersService
                .rejectOrder(order.id, result.justification)
                .pipe(finalize(() => loader.close()))
                .subscribe({
                  next: () => {
                    this.dialog.open(AlertComponent, {
                      disableClose: true,
                      data: {
                        title: 'alert.information',
                        message: 'orders-dashboard.rejectOrderSuccess',
                        param: { orderId: order.id, projectDescription: order.projectDescription },
                      },
                    });
                    this.resetTable();
                  },
                  error: () => {
                    this.dialog.open(AlertComponent, {
                      disableClose: true,
                      data: {
                        title: 'alert.information',
                        message: 'orders-dashboard.rejectOrderError',
                        param: { orderId: order.id },
                      },
                    });
                  },
                });
            }
          });
        }
      });
  }

  deleteOrder(order: OrderProjection) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          message: 'orders-dashboard.deleteOrderConfirmation',
          param: { orderId: order.id, projectDescription: order.projectDescription },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.confirm) {
          this.ordersService.deactivateOrder(order.id).subscribe({
            next: () => {
              this.dialog.open(AlertComponent, {
                disableClose: true,
                data: {
                  title: 'alert.information',
                  message: 'orders-dashboard.deleteOrderSuccess',
                  param: { orderId: order.id, projectDescription: order.projectDescription },
                },
              });
              this.resetTable();
            },
          });
        }
      });
  }

  navigateToEditOrder(order: OrderProjection) {
    this.ordersService.getOrderItems(order.id).subscribe({
      next: () => {
        this.router.navigate(['/new-order', order.id]);
      },
      error: () => {},
    });
  }

  sendOrder(order: OrderProjection) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          message: 'orders-dashboard.sendOrderConfirmation',
          param: { orderId: order.id, projectDescription: order.projectDescription },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.confirm) {
          const loader = this.dialog.open(LoaderDialogComponent, {
            disableClose: true,
          });

          this.ordersService
            .sendOrder(order.id)
            .pipe(finalize(() => loader.close()))
            .subscribe({
              next: () => {
                this.dialog.open(AlertComponent, {
                  disableClose: true,
                  data: {
                    title: 'alert.information',
                    message: 'orders-dashboard.sendOrderSuccess',
                    param: {
                      orderId: order.id,
                      projectDescription: order.projectDescription,
                    },
                  },
                });
                this.resetTable();
              },
              error: () => {
                this.dialog.open(AlertComponent, {
                  disableClose: true,
                  data: {
                    title: 'alert.information',
                    message: 'orders-dashboard.error.send',
                  },
                });
              },
            });
        }
      });
  }

  cancelOrder(order: OrderProjection) {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: true,
        data: {
          message: 'orders-dashboard.cancelOrderConfirmation',
          param: { orderId: order.id, projectDescription: order.projectDescription },
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.confirm) {
          const justificationDialogRef = this.dialog.open(
            ConfirmFormDialogComponent,
            {
              data: {
                disableClose: true,
                title: 'alert.cancel-justification',
                message: 'alert.cancel-dialog-description',
              },
            }
          );

          justificationDialogRef.afterClosed().subscribe((result) => {
            if (result?.confirm) {
              const loader = this.dialog.open(LoaderDialogComponent, {
                disableClose: true,
              });
              this.ordersService
                .cancelOrder(order.id, result.justification)
                .pipe(finalize(() => loader.close()))
                .subscribe({
                  next: () => {
                    this.dialog.open(AlertComponent, {
                      disableClose: true,
                      data: {
                        title: 'alert.confirmation',
                        message: 'orders-dashboard.cancelOrderSuccess',
                        param: {
                          orderId: order.id,
                          projectDescription: order.projectDescription,
                        },
                      },
                    });
                    this.resetTable();
                  },
                });
            }
          });
        }
      });
  }

  resetForm() {
    this.orderFormViewChild.resetForm();
    this.pageChange(this.pageable);
  }

  resetTable() {
    this.pageChange(this.pageable);
  }

  removeLocalStorage() {
    this.localStorageService.clearAll();
  }
}
